/**
 * Edit Inventory - Update existing inventory item
 * Loads data from API before rendering form
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Package } from 'lucide-react';
import {
  inventoryApi,
  type Inventory,
  type InventoryType,
  type InventoryUnit,
} from '../../../services/gaushala/api';

export default function EditInventory() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [inventoryTypes, setInventoryTypes] = useState<InventoryType[]>([]);
  const [inventoryUnits, setInventoryUnits] = useState<InventoryUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<Partial<Inventory>>({});

  // Validation constraints
  const VALIDATION_RULES = {
    itemName: { min: 2, max: 100 },
    quantity: { min: 0, max: 9999999.99 },
    minimumStockLevel: { min: 0, max: 9999999.99 },
    supplier: { min: 0, max: 100 },
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const [inventoryResponse, typesResponse, unitsResponse] = await Promise.all([
        inventoryApi.getInventoryById(parseInt(id)),
        inventoryApi.getInventoryTypes(),
        inventoryApi.getInventoryUnits(),
      ]);

      if (inventoryResponse.success && inventoryResponse.data) {
        setFormData(inventoryResponse.data);
      }
      if (typesResponse.success && typesResponse.data) {
        setInventoryTypes(typesResponse.data);
      }
      if (unitsResponse.success && unitsResponse.data) {
        setInventoryUnits(unitsResponse.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Item Name validation
    if (!formData.itemName?.trim()) {
      newErrors.itemName = 'Please enter item name';
    } else if (formData.itemName.trim().length < VALIDATION_RULES.itemName.min) {
      newErrors.itemName = `Item name must be at least ${VALIDATION_RULES.itemName.min} characters`;
    } else if (formData.itemName.length > VALIDATION_RULES.itemName.max) {
      newErrors.itemName = `Item name cannot exceed ${VALIDATION_RULES.itemName.max} characters`;
    }

    // Quantity validation
    if ((formData.quantity ?? 0) < VALIDATION_RULES.quantity.min) {
      newErrors.quantity = `Quantity must be at least ${VALIDATION_RULES.quantity.min}`;
    } else if ((formData.quantity ?? 0) > VALIDATION_RULES.quantity.max) {
      newErrors.quantity = `Quantity cannot exceed ${VALIDATION_RULES.quantity.max}`;
    }

    // Minimum Stock Level validation
    if ((formData.minimumStockLevel ?? 0) < VALIDATION_RULES.minimumStockLevel.min) {
      newErrors.minimumStockLevel = `Minimum stock level must be at least ${VALIDATION_RULES.minimumStockLevel.min}`;
    } else if ((formData.minimumStockLevel ?? 0) > VALIDATION_RULES.minimumStockLevel.max) {
      newErrors.minimumStockLevel = `Minimum stock level cannot exceed ${VALIDATION_RULES.minimumStockLevel.max}`;
    }

    // Supplier validation (optional field, but has max length)
    if (formData.supplier && formData.supplier.length > VALIDATION_RULES.supplier.max) {
      newErrors.supplier = `Supplier name cannot exceed ${VALIDATION_RULES.supplier.max} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!id) return;

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const response = await inventoryApi.updateInventory(parseInt(id), formData);

      if (response.success) {
        alert('Inventory updated successfully!');
        navigate('/gaushala/inventory');
      } else {
        alert('Failed to update inventory: ' + response.error);
      }
    } catch (error) {
      console.error('Error updating inventory:', error);
      alert('Error updating inventory');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof Inventory, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate('/gaushala/inventory')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={20} />
        Back
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="rounded-full bg-blue-100 p-3">
          <Package className="text-blue-600" size={24} />
        </div>
        <h1 className="text-2xl font-bold">Edit Inventory</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Item Name *
            <span className="text-xs text-gray-500 font-normal ml-2">({(formData.itemName || '').length}/{VALIDATION_RULES.itemName.max})</span>
          </label>
          <input
            type="text"
            value={formData.itemName || ''}
            onChange={(e) => {
              if (e.target.value.length <= VALIDATION_RULES.itemName.max) {
                handleChange('itemName', e.target.value);
              }
            }}
            maxLength={VALIDATION_RULES.itemName.max}
            className={`w-full rounded-lg border px-4 py-2 ${
              errors.itemName ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            required
          />
          {errors.itemName && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <span>⚠️</span>
              {errors.itemName}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
          <select
            value={formData.inventoryTypeId || 0}
            onChange={(e) => handleChange('inventoryTypeId', parseInt(e.target.value))}
            className="w-full rounded-lg border px-4 py-2"
            required
          >
            {inventoryTypes.map((type) => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Unit *</label>
          <select
            value={formData.inventoryUnitId || 0}
            onChange={(e) => handleChange('inventoryUnitId', parseInt(e.target.value))}
            className="w-full rounded-lg border px-4 py-2"
            required
          >
            {inventoryUnits.map((unit) => (
              <option key={unit.id} value={unit.id}>{unit.unitName}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity *
            <span className="text-xs text-gray-500 font-normal ml-2">(Max: {VALIDATION_RULES.quantity.max})</span>
          </label>
          <input
            type="number"
            min={VALIDATION_RULES.quantity.min}
            max={VALIDATION_RULES.quantity.max}
            step="0.01"
            value={formData.quantity || 0}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              if (value <= VALIDATION_RULES.quantity.max) {
                handleChange('quantity', value);
              }
            }}
            className={`w-full rounded-lg border px-4 py-2 ${
              errors.quantity ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            required
          />
          {errors.quantity && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <span>⚠️</span>
              {errors.quantity}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Minimum Stock Level *
            <span className="text-xs text-gray-500 font-normal ml-2">(Max: {VALIDATION_RULES.minimumStockLevel.max})</span>
          </label>
          <input
            type="number"
            min={VALIDATION_RULES.minimumStockLevel.min}
            max={VALIDATION_RULES.minimumStockLevel.max}
            step="0.01"
            value={formData.minimumStockLevel || 0}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              if (value <= VALIDATION_RULES.minimumStockLevel.max) {
                handleChange('minimumStockLevel', value);
              }
            }}
            className={`w-full rounded-lg border px-4 py-2 ${
              errors.minimumStockLevel ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            required
          />
          {errors.minimumStockLevel && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <span>⚠️</span>
              {errors.minimumStockLevel}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            Alert will be shown when quantity falls below this level
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Supplier
            <span className="text-xs text-gray-500 font-normal ml-2">({(formData.supplier || '').length}/{VALIDATION_RULES.supplier.max})</span>
          </label>
          <input
            type="text"
            value={formData.supplier || ''}
            onChange={(e) => {
              if (e.target.value.length <= VALIDATION_RULES.supplier.max) {
                handleChange('supplier', e.target.value);
              }
            }}
            maxLength={VALIDATION_RULES.supplier.max}
            className={`w-full rounded-lg border px-4 py-2 ${
              errors.supplier ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
          />
          {errors.supplier && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <span>⚠️</span>
              {errors.supplier}
            </p>
          )}
        </div>

        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Save size={20} />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/gaushala/inventory')}
            className="px-6 py-2 border rounded-lg"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
